import { useCallback, KeyboardEvent } from 'react';
import { Typography } from '@mui/material';
import { DisclosureIcon } from '@wavetrak/theme';
import { trackEvent, getWindow } from '@surfline/web-common';
import PremiumPill from 'components/Icons/PremiumPill';
import styles from './FeaturePaywall.module.scss';

interface FeaturePaywallProps {
  text?: string;
  segmentProperties: Record<string, any>;
  upgradeUrl: string;
  dataTestId?: string;
  showPremiumPill?: boolean;
}

const FeaturePaywall = ({
  text = 'View hourly forecast data',
  segmentProperties,
  upgradeUrl,
  dataTestId = 'feature-paywall',
  showPremiumPill = false,
}: FeaturePaywallProps) => {
  const handleClick = useCallback(() => {
    trackEvent('Clicked Subscribe CTA', segmentProperties);
    getWindow()?.location.assign(upgradeUrl);
  }, [segmentProperties, upgradeUrl]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleClick();
      }
    },
    [handleClick],
  );

  return (
    <div
      className={styles.container}
      data-testid={dataTestId}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <span className={styles.textWrapper}>
        <span className={styles.labelWithPill}>
          <Typography variant="callout1" component="p" className={styles.text}>
            {text}
          </Typography>
          {showPremiumPill && (
            <span className={styles.premiumPillWrapper}>
              <PremiumPill />
            </span>
          )}
        </span>
        <span className={styles.arrow} aria-hidden="true">
          <DisclosureIcon rotation={90} size={14} color="currentColor" />
        </span>
      </span>
    </div>
  );
};

export default FeaturePaywall;
